import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import {
  GoCardFieldControl,
  type GoCardFieldCommitResult,
} from "@/components/go/GoCardFieldControl";
import type { GoEntityCardFieldValue, GoFieldProvenancePatch } from "@/types/go-entity-card";

type FillFilter = "all" | "filled" | "empty";

type Props = {
  fields: GoEntityCardFieldValue[];
  loading?: boolean;
  message?: string | null;
  editing?: boolean;
  highlightedFieldKey?: string | null;
  savingFieldKeys?: ReadonlySet<string>;
  onCommit?: (fieldKey: string, value: unknown) => Promise<GoCardFieldCommitResult | void>;
  onMarkNa?: (fieldKey: string) => Promise<GoCardFieldCommitResult | void>;
  onClearNa?: (fieldKey: string) => Promise<GoCardFieldCommitResult | void>;
  onSaveProvenance?: (
    fieldKey: string,
    patch: GoFieldProvenancePatch,
  ) => Promise<GoCardFieldCommitResult | void>;
  onFocusField?: (fieldKey: string) => void;
};

const HUB_TAB_LABELS: Record<string, string> = {
  overview: "Обзор / прочее",
  geography: "География",
  society: "Общество",
  economy: "Экономика",
  sensitive: "Чувствительные",
  passport: "Паспорт",
};

function isFilled(fv: GoEntityCardFieldValue): boolean {
  if (fv.is_masked || fv.is_na) return true;
  const v = fv.value;
  if (v == null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  return true;
}

function groupKey(fv: GoEntityCardFieldValue): string {
  return (fv.hub_tab || "overview").toLowerCase();
}

/**
 * Просмотр/правка всех полей ok20: поиск + фильтр заполненности + группы по hub_tab.
 */
export function CountryFieldsBrowser({
  fields,
  loading,
  message,
  editing = false,
  highlightedFieldKey,
  savingFieldKeys,
  onCommit,
  onMarkNa,
  onClearNa,
  onSaveProvenance,
  onFocusField,
}: Props) {
  const [q, setQ] = useState("");
  const [fill, setFill] = useState<FillFilter>("filled");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return fields.filter((fv) => {
      if (fill === "filled" && !isFilled(fv)) return false;
      if (fill === "empty" && isFilled(fv)) return false;
      if (!needle) return true;
      return (
        fv.field_key.toLowerCase().includes(needle) ||
        (fv.label || "").toLowerCase().includes(needle)
      );
    });
  }, [fields, q, fill]);

  const groups = useMemo(() => {
    const map = new Map<string, GoEntityCardFieldValue[]>();
    for (const fv of filtered) {
      const k = groupKey(fv);
      const list = map.get(k) ?? [];
      list.push(fv);
      map.set(k, list);
    }
    const order = ["overview", "geography", "society", "economy", "sensitive", "passport"];
    return [...map.entries()].sort((a, b) => {
      const ia = order.indexOf(a[0]);
      const ib = order.indexOf(b[0]);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a[0].localeCompare(b[0]);
    });
  }, [filtered]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
          Все поля шаблона
          <SectionHelpTooltip label="Справка: все поля">
            <p className="font-medium">Зачем</p>
            <p>Полный список ok20 выбранной страны. Обзор остаётся паспортом.</p>
            <p className="mt-2 font-medium">Как работать</p>
            <ul className="list-disc pl-4">
              <li>Поиск — по названию или коду поля (`1.1.4`).</li>
              <li>Фильтр «заполненные» скрывает пустые ячейки.</li>
              <li>
                Правка: кнопка «Редактировать карточку» → blur сохраняет поле. Esc — выход из
                правки.
              </li>
              <li>N/A у поля; Ctrl+Shift+N — для сфокусированного; «Снять N/A» возвращает ввод.</li>
              <li>Импорт пакетов стран — в админке ГО, не в тулбаре workspace.</li>
            </ul>
          </SectionHelpTooltip>
        </div>
        <span className="text-[11px] text-muted-foreground">
          показано {filtered.length} / {fields.length}
          {editing ? " · режим правки" : ""}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="min-w-[200px] flex-1 space-y-1">
          <Label htmlFor="go-fields-q">Поиск</Label>
          <Input
            id="go-fields-q"
            placeholder="Столица, 1.1.1.1, ISO…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="w-[180px] space-y-1">
          <Label>Заполненность</Label>
          <Select value={fill} onValueChange={(v) => setFill(v as FillFilter)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[250]">
              <SelectItem value="filled">Только заполненные</SelectItem>
              <SelectItem value="empty">Только пустые</SelectItem>
              <SelectItem value="all">Все</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground">Загрузка полей…</div>
      ) : fields.length === 0 ? (
        <div className="text-xs text-muted-foreground">{message ?? "Нет полей шаблона"}</div>
      ) : filtered.length === 0 ? (
        <div className="text-xs text-muted-foreground">Нет совпадений — смените поиск или фильтр.</div>
      ) : (
        <div className="max-h-[min(70vh,640px)] space-y-4 overflow-y-auto pr-1">
          {groups.map(([tab, items]) => (
            <section key={tab} className="space-y-2">
              <h3 className="sticky top-0 z-[1] bg-card/95 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur">
                {HUB_TAB_LABELS[tab] ?? tab} · {items.length}
              </h3>
              <div className="space-y-2">
                {items.map((fv) => (
                  <div
                    key={fv.field_key}
                    className="rounded-md border border-border bg-muted/15 px-3 py-2"
                  >
                    <GoCardFieldControl
                      field={fv}
                      editing={editing}
                      highlighted={highlightedFieldKey === fv.field_key}
                      saving={savingFieldKeys?.has(fv.field_key)}
                      showFieldKey
                      onCommit={onCommit}
                      onMarkNa={onMarkNa}
                      onClearNa={onClearNa}
                      onSaveProvenance={onSaveProvenance}
                      onFocusField={onFocusField}
                    />
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
